// Screen · Year plan (route /roadmap) — 2026-09-30 redesign of the Roadmap.
//
// The whole year for one class (or any grade), one term at a time, one row per week. Every row
// opens that week's lesson page — the title IS the link (before, titles looked clickable and were
// not; only two tiny "pack"/"sheet" chips worked). The worksheet and Teacher Pack sit at the end of
// each row. This week is marked and scrolled into view. No internal codes, no coverage arithmetic.
//
// ?class=<id>  a class (its plans mark the weeks: planned / approved)
// ?grade=<n>   browse any grade without a class (the Lead, or a teacher looking ahead)
// ?term=<n>    which term's tab is open (default: the term this week falls in)

import { api } from "../api";
import { navigate } from "../router";
import { esc } from "../dom";
import { t } from "../i18n";
import { icon } from "../icons";
import { me } from "../state";
import { loadGrade, loadVocab, gradeFromClass, termNumber, type CurriculumNode, type GradeCurriculum, type ClassPosition, type VocabRegister } from "../curriculum";
import { materialsForGrade, type NodeMaterials } from "../materials";
import { resolveClassLesson, gradeLessonFor, schoolWeekNow, noStepLine, plansReachLine, weekChips, gradeLine, byGrade, termPhase, termNote, plainText, taskLabel, wordsOf, type ClassRow, type TermInfo, type WeekChip } from "../lesson-data";
import { pageHeader, emptyState, linkTabs, statusChip, bestState, rangeLabel, type PlanState } from "../ui";
import { loadLessonRefs, teacherTitle, type LessonRef } from "../lesson-refs";
import { termWeekRange } from "../term-weeks";
import { schoolWeekDates } from "../calendar-events";
import "../styles/roadmap.css";
import { loadWeeklyPreparation, mountWeeklyPreparation } from "../weekly-preparation";

const SELECTED_KEY = "eden:selectedClass";
/** Why "Week 2" can be the third calendar week since school began: the school counts five school days as one week (owner ruling 2026-10-07). */
const WEEK_RULE_NOTE = "A week is five school days, so a public holiday (like Pchum Ben) doesn't use one up.";

export function selectedClassId(): string | null {
  try {
    return localStorage.getItem(SELECTED_KEY);
  } catch {
    return null;
  }
}
export function rememberClass(id: string): void {
  try {
    localStorage.setItem(SELECTED_KEY, id);
  } catch {
    /* private mode — selection just doesn't stick */
  }
}

/** A class <select> (kept for pages that still use one). */
export function classPicker(classes: { id: string; name: string }[], picked: { id: string; name: string }): string {
  if (classes.length <= 1) return `<span class="class-badge">${esc(picked.name)}</span>`;
  return `<select id="rm-class" class="classpick" aria-label="${t("week.class_label")}">${classes
    .map((c) => `<option value="${esc(c.id)}"${c.id === picked.id ? " selected" : ""}>${esc(c.name)}</option>`)
    .join("")}</select>`;
}

/** Pick the remembered class, else the first. */
export function pickClass<T extends { id: string }>(classes: T[]): T | null {
  const remembered = selectedClassId();
  return classes.find((c) => c.id === remembered) ?? classes[0] ?? null;
}

/** A week's link: the lesson page, carrying the class AND the school term and week that lesson belongs to (owner
 *  ruling A3, 21 Oct 2026), so "Plan another lesson" there opens the form on the same week. A term whose row is not
 *  known, or whose week count does not reach the lesson's week, carries the class alone — as the link always did. */
type TermWeeks = Map<number, { id: string; weekCount: number }>;
function weekHref(n: CurriculumNode, cls: ClassRow | null, termWeeks: TermWeeks): string {
  if (!cls) return `/map/${encodeURIComponent(n.id)}`;
  const q = new URLSearchParams({ class: cls.id });
  const row = termWeeks.get(n.term);
  if (row && n.suggested_week >= 1 && n.suggested_week <= row.weekCount) {
    q.set("term", row.id);
    q.set("week", String(n.suggested_week));
  }
  return `/map/${encodeURIComponent(n.id)}?${q}`;
}

/** A chip's words, with the dates it covers as its tooltip. */
const chipTag = (c: WeekChip): string => `<span class="tag"${c.when ? ` title="${esc(c.when)}"` : ""}>${esc(c.label)}</span>`;

/** The files at the end of a week row — plain links, each in its own tab. Later terms are drafts. */
function fileChips(m: NodeMaterials | undefined, draft: boolean): string {
  if (!m) return "";
  const tag = draft ? ` <span class="draft-tag">draft</span>` : "";
  const sheet = m.sheet
    ? `<a class="chip-link" href="${esc(m.sheet)}" target="_blank" rel="noopener" data-file="sheet" title="Worksheet">${icon("sheet", "ico-sm")}<span>Worksheet</span>${tag}</a>`
    : "";
  const pack = m.pack
    ? `<a class="chip-link" href="${esc(m.pack)}" target="_blank" rel="noopener" data-file="pack" title="Teacher Pack">${icon("pack", "ico-sm")}<span>Teacher Pack</span>${tag}</a>`
    : "";
  return sheet || pack ? `<div class="wk-files">${sheet}${pack}</div>` : "";
}

function weekRow(n: CurriculumNode, grade: number, nowId: string | null, nowLabel: string, pos: ClassPosition | null, states: Map<string, PlanState>, mats: Map<string, NodeMaterials>, href: string, lead = false): string {
  const now = n.id === nowId;
  const approved = !!pos?.approved.has(n.seq);
  const planned = !!pos?.planned.has(n.seq);
  const quiet = n.type === "anchor" || n.type === "buffer";
  const events = weekChips(n, grade);
  // The same words Home and My plans use (Draft · Submitted · Approved) for this term's plans — and, for
  // the Lead, the Lead's own words (a sent plan is "To review", as on Home and Team); a plan from another
  // term, which this page does not list, reads "Planned".
  const known = states.get(n.id);
  const state = known
    ? statusChip(known, lead ? { lead: true } : undefined)
    : approved
      ? statusChip("approved")
      : planned ? `<span class="chip st st-submitted">Planned</span>` : "";
  return `<li class="wk-row${now ? " now" : ""}${quiet ? " quiet" : ""}${approved || planned ? " done" : ""}" data-node="${esc(n.id)}">
    <a class="wk-main" data-link href="${esc(href)}">
      <span class="wk-num" aria-label="Week ${n.suggested_week}">W${n.suggested_week}</span>
      <span class="wk-text">
        <span class="wk-title">${quiet ? icon("flag", "ico-sm") : ""}${esc(teacherTitle(n))}</span>
        ${events.length || now ? `<span class="wk-tags">${now ? `<span class="tag tag-now">${esc(nowLabel)}</span>` : ""}${events.map(chipTag).join("")}</span>` : ""}
      </span>
      ${state}
    </a>
    ${fileChips(mats.get(n.id), n.term > 1)}
  </li>`;
}

// ── the visual year map (teacher review, 15 Oct 2026, point 9) ─────────────────────────────────────
// "They want to see the entire content roadmap with the key points of each week, so each teacher SEES where they are
// and what exactly they need to cover — the week they are on blinking." The map draws the term as a path of weeks:
// done weeks ticked, this week pulsing ("You are here"), next week marked, each stop carrying its key points — the
// book pages, the grammar, the Cambridge words and exam practice, the school events — in plain words.

interface KeyPoint { ico: Parameters<typeof icon>[0]; text: string }

const clip = (s: string, n: number): string => (s.length > n ? `${s.slice(0, n - 1).trimEnd()}…` : s);

/** A week's key points, in the order a teacher prepares them. Generated from the curriculum, never typed. */
function keyPoints(n: CurriculumNode, ref: LessonRef | undefined, reg: VocabRegister | null): KeyPoint[] {
  const out: KeyPoint[] = [];
  const cb = ref?.cb && !("none" in ref.cb) ? ref.cb : null;
  if (cb) {
    const pages = cb.parts.map((p) => p[1]).filter(Boolean).join(", ");
    out.push({ ico: "book", text: `${cb.book.replace(/ Student Book$/, "")}${cb.unit ? ` · ${cb.unit.replace(/:.*$/, "")}` : ""}${pages ? ` · ${/[–,-]/.test(pages) ? "pages" : "page"} ${pages}` : ""}` });
  }
  const grammar = plainText(n.coursebook?.grammar ?? "");
  if (grammar) out.push({ ico: "puzzle", text: clip(`Grammar: ${grammar}`, 110) });
  const words = wordsOf(reg, n.thread);
  if (words?.words.length) out.push({ ico: "plans", text: clip(`Words: ${words.words.slice(0, 6).join(", ")}${words.words.length > 6 ? ` … (${words.words.length})` : ""}`, 110) });
  else if (words?.review) out.push({ ico: "plans", text: "Review earlier Cambridge words" });
  const task = taskLabel(n.thread?.task_family);
  if (task) out.push({ ico: "tests", text: `Cambridge practice: ${task}` });
  return out;
}

function mapView(nodes: CurriculumNode[], grade: number, nowNode: CurriculumNode | null, nextId: string | null, nowLabel: string, pos: ClassPosition | null, states: Map<string, PlanState>,
  mats: Map<string, NodeMaterials>, refs: Record<string, LessonRef> | null, reg: VocabRegister | null, hrefOf: (n: CurriculumNode) => string, lead: boolean, dates: ((week: number) => string) | null): string {
  const nowSeq = nowNode?.seq ?? null;
  // Measured on the whole year's order, so a past term reads as done and a later term as coming.
  const stopState = (n: CurriculumNode): "done" | "now" | "next" | "later" => {
    if (nowSeq === null) return "later";
    if (n.seq === nowSeq) return "now";
    if (n.seq < nowSeq) return "done";
    return n.id === nextId ? "next" : "later";
  };
  // the term at a glance: one dot per week, the current one pulsing; a dot jumps to its stop
  const dots = nodes.map((n) => {
    const st = stopState(n);
    const ev = weekChips(n, grade);
    return `<a class="rm-dot rm-${st}${ev.length ? " has-event" : ""}" href="#wk-${esc(n.id)}" data-rm-jump="wk-${esc(n.id)}" title="Week ${n.suggested_week}: ${esc(teacherTitle(n))}" aria-label="Week ${n.suggested_week}${st === "now" ? " (this week)" : ""}"><span>${n.suggested_week}</span></a>`;
  }).join("");
  const stops = nodes.map((n) => {
    const st = stopState(n);
    const known = states.get(n.id);
    const planned = !!pos?.planned.has(n.seq) || !!pos?.approved.has(n.seq);
    const chip = known ? statusChip(known, lead ? { lead: true } : undefined) : planned ? `<span class="chip st st-submitted">Planned</span>` : "";
    // a past week is ticked only on real evidence — taught, or a plan handed in — never for a draft or the calendar alone
    const ticked = st === "done" && (!!pos?.taught.has(n.seq) || !!pos?.approved.has(n.seq) || known === "submitted" || known === "approved");
    const keys = keyPoints(n, refs?.[n.id], reg);
    const events = weekChips(n, grade);
    const sheet = mats.get(n.id)?.sheet;
    const when = dates ? dates(n.suggested_week) : "";
    const flag = st === "now" ? `<span class="rm-here">${icon("flag", "ico-sm")}${esc(nowLabel === "This week" ? "You are here · this week" : nowLabel)}</span>`
      : st === "next" ? `<span class="rm-nextlabel">Next week</span>` : "";
    return `<li class="rm-stop rm-${st}${n.type === "anchor" || n.type === "buffer" ? " rm-quiet" : ""}" id="wk-${esc(n.id)}" data-node="${esc(n.id)}">
      <span class="rm-pin" aria-hidden="true">${ticked ? icon("check", "ico-sm") : `W${n.suggested_week}`}</span>
      <a class="rm-card" data-link href="${esc(hrefOf(n))}">
        <span class="rm-top"><span class="rm-week">Week ${n.suggested_week}${when ? ` · ${esc(when)}` : ""}</span>${flag}${chip}</span>
        <b class="rm-title">${esc(teacherTitle(n, refs?.[n.id]))}</b>
        ${keys.length ? `<ul class="rm-keys">${keys.map((k) => `<li>${icon(k.ico, "ico-sm")}<span>${esc(k.text)}</span></li>`).join("")}</ul>` : ""}
        ${events.length || sheet ? `<span class="rm-tags">${events.map(chipTag).join("")}${sheet ? `<span class="tag tag-sheet">${icon("sheet", "ico-sm")}Worksheet ready</span>` : ""}</span>` : ""}
      </a>
    </li>`;
  }).join("");
  const done = nodes.filter((n) => stopState(n) === "done").length;
  const pct = nodes.length ? Math.round((done / nodes.length) * 100) : 0;
  return `<section class="rm" aria-label="The term as a map">
    <div class="rm-strip">
      <div class="rm-progress" aria-hidden="true"><span style="width:${pct}%"></span></div>
      <nav class="rm-dots" aria-label="Weeks of the term">${dots}</nav>
      <p class="rm-legend small muted"><span class="lg-item"><span class="lg lg-done"></span>Past</span><span class="lg-item"><span class="lg lg-now"></span>This week</span><span class="lg-item"><span class="lg lg-later"></span>Coming</span><span class="lg-item">Tap a week to see it</span><span class="lg-item lg-note">${WEEK_RULE_NOTE}</span></p>
    </div>
    <ol class="rm-path">${stops}</ol>
  </section>`;
}

export async function renderRoadmap(page: HTMLElement, _params: Record<string, string>, query: URLSearchParams): Promise<void> {
  const user = me();
  const isLead = user?.auth_role === "lead";
  const [{ classes: ownRaw }, termInfo, refsFile] = await Promise.all([
    api<{ classes: ClassRow[] }>("/api/classes?scope=own"),
    api<TermInfo | null>("/api/terms/current").catch(() => null),
    loadLessonRefs(), // week titles name the unit ("Unit 1: I'm staying in tonight · Lessons 2–3")
  ]);
  const own = [...ownRaw].sort(byGrade);

  // Which class (or grade) is open: ?class= wins, then ?grade= (browse mode), then the remembered class.
  const qClass = query.get("class");
  const qGrade = Number(query.get("grade")) || null;
  let cls: ClassRow | null = qClass ? own.find((c) => c.id === qClass) ?? null : null;
  if (!cls && qClass && isLead) {
    const all = await api<{ classes: ClassRow[] }>("/api/classes").catch(() => ({ classes: [] as ClassRow[] }));
    cls = all.classes.find((c) => c.id === qClass) ?? null;
  }
  if (!cls && !qGrade) cls = pickClass(own);
  const grade = cls ? gradeFromClass(cls) : qGrade;
  if (cls && own.some((c) => c.id === cls!.id)) rememberClass(cls.id);

  // The picker: your classes first, then every grade to browse.
  const opts = [
    own.length ? `<optgroup label="Your classes">${own.map((c) => `<option value="class:${esc(c.id)}"${cls?.id === c.id ? " selected" : ""}>${esc(c.name)}${c.level ? ` · ${esc(c.level)}` : ""}</option>`).join("")}</optgroup>` : "",
    `<optgroup label="Any grade">${Array.from({ length: 10 }, (_, i) => i + 1)
      .map((g) => `<option value="grade:${g}"${!cls && grade === g ? " selected" : ""}>Grade ${g}</option>`).join("")}</optgroup>`,
  ].join("");
  const picker = `<label class="inline-field"><span class="sr-only">Class or grade</span><select id="yp-pick" aria-label="Class or grade">${opts}</select></label>`;

  const cur = grade ? await loadGrade(grade) : null;
  if (!cur) {
    page.innerHTML = `${pageHeader({ title: t("nav.year_plan"), actions: picker })}
      ${grade
        ? emptyState(grade >= 11 ? "Grades 11–12 are not in the year plan yet." : t("week.no_curriculum"))
        : emptyState("Pick a class or a grade to see its year plan.")}`;
    wire(page);
    return;
  }

  // This week's lesson — the school calendar's week, the same for a class, for "Any grade" and for every teacher of the
  // grade (owner ruling A2, 21 Oct 2026). A class's plans mark the weeks they cover; they never move "this week".
  let nowId: string | null = null;
  let noStep = false;
  let pos: ClassPosition | null = null;
  const states = new Map<string, PlanState>();
  if (cls) {
    // The class's lessons this term, whoever wrote them: a class the Lead moved keeps the plans its first teacher wrote.
    const termLessons = await api<{ lessons: { node_id: string | null; status: PlanState; week_no: number }[] }>(`/api/classes/${encodeURIComponent(cls.id)}/lessons`)
      .then((r) => r.lessons).catch(() => [] as { node_id: string | null; status: PlanState; week_no: number }[]);
    const cl = await resolveClassLesson(cls, termInfo);
    nowId = cl.node?.id ?? null;
    noStep = cl.noStep;
    pos = cl.pos;
    for (const l of termLessons) {
      if (!l.node_id) continue;
      states.set(l.node_id, bestState([l.status, states.get(l.node_id) ?? "none"]));
    }
  } else if (termInfo) {
    const hit = gradeLessonFor(cur, schoolWeekNow(termInfo));
    nowId = hit.node?.id ?? null;
    noStep = hit.noStep;
  }
  const nowNode = cur.nodes.find((n) => n.id === nowId) ?? null;
  const termTab = Number(query.get("term")) || nowNode?.term || (termInfo ? termNumber(termInfo.term) : null) || 1;
  const mats = await materialsForGrade(cur.grade);
  // The school term rows this page knows: the running term now, the rest once the lesson counts arrive (below).
  const termWeeks: TermWeeks = new Map();
  const curTermN = termInfo ? termNumber(termInfo.term) : null;
  if (termInfo && curTermN) termWeeks.set(curTermN, { id: termInfo.term.id, weekCount: termInfo.term.week_count ?? termInfo.weeks?.length ?? 0 });
  const hrefOf = (n: CurriculumNode): string => weekHref(n, cls, termWeeks);
  const base = cls ? `/roadmap?class=${encodeURIComponent(cls.id)}` : `/roadmap?grade=${cur.grade}`;

  const view = query.get("view") === "list" ? "list" : "map";
  const viewQ = view === "list" ? "&view=list" : "";
  const tabs = linkTabs([1, 2, 3].map((tn) => ({ label: `Term ${tn}`, href: `${base}&term=${tn}${viewQ}`, on: tn === termTab })), "Terms");
  const viewTabs = `<nav class="rm-views" aria-label="How to show the year">
      <a class="rm-view${view === "map" ? " on" : ""}" data-link href="${base}&term=${termTab}"${view === "map" ? ' aria-current="page"' : ""}>${icon("map", "ico-sm")}<span>Map</span></a>
      <a class="rm-view${view === "list" ? " on" : ""}" data-link href="${base}&term=${termTab}&view=list"${view === "list" ? ' aria-current="page"' : ""}>${icon("plans", "ico-sm")}<span>List</span></a>
    </nav>`;
  const schoolWeek = schoolWeekNow(termInfo);
  const nowLabel = schoolWeek?.kind === "week" || !schoolWeek ? "This week" : schoolWeek.kind === "last" ? "Last week" : "First week";
  const termNodes = cur.nodes.filter((n) => n.term === termTab);
  // The week's dates, for the term that is running (the only term whose dates this screen knows).
  const dates = termInfo && curTermN === termTab && termInfo.term.end_date
    ? (week: number): string => {
        // a year-plan week the school calendar does not have (Term 1 has 13 weeks; the plans of Grades 1 and 5-10 carry a 14th)
        // has no dates: termWeekRange would run on into the next term's first week
        if (!schoolWeekDates(termTab, week)) return "";
        const r = termWeekRange({ start_date: termInfo.term.start_date, end_date: termInfo.term.end_date! }, week);
        return rangeLabel(r.start, r.end);
      }
    : null;
  const reg = view === "map" ? await loadVocab(cur.grade).catch(() => null) : null;
  const rows = view === "list" ? termNodes.map((n) => weekRow(n, cur.grade, nowId, nowLabel, pos, states, mats, hrefOf(n), isLead)).join("") : "";
  const nextId = nowNode ? [...cur.nodes].filter((n) => n.seq > nowNode.seq).sort((a, b) => a.seq - b.seq)[0]?.id ?? null : null;
  const mapHtml = view === "map" ? mapView(termNodes, cur.grade, nowNode, nextId, nowLabel, pos, states, mats, refsFile?.nodes ?? null, reg, hrefOf, isLead, dates) : "";
  const sheets = [...mats.values()].filter((m) => m.term === termTab && m.sheet).length;
  const legend = `<p class="muted small yp-legend">${cur.nodes.length} teaching weeks this year · ${sheets} worksheet${sheets === 1 ? "" : "s"} in Term ${termTab}${termTab > 1 ? " (drafts)" : ""}.${view === "list" ? ` ${WEEK_RULE_NOTE}` : ""}</p>`;
  const nowBanner = nowNode
    ? `<a class="now-banner" data-link href="${esc(hrefOf(nowNode))}">
        <span class="nb-label">${esc(nowLabel)}</span>
        <span class="nb-title">Term ${nowNode.term} · Week ${nowNode.suggested_week} — ${esc(teacherTitle(nowNode))}</span>
        ${icon("chevron")}
      </a>`
    : "";
  // Neutral lines only (no comparison words, no colour): a school week the year plan has no step for, and how far the
  // class's own plans reach — where they are, not whether that is good.
  const lines = [noStep ? noStepLine(schoolWeek) : "", plansReachLine(pos)].filter(Boolean);
  const weekNotes = lines.length ? `<p class="muted small" data-week-notes>${lines.map(esc).join(" ")}</p>` : "";

  page.innerHTML = `
    ${pageHeader({
      eyebrow: gradeLine(cur),
      title: t("nav.year_plan"),
      sub: cls ? `${esc(cls.name)} — where the class is, and what each week covers. Tap a week to open its lesson.` : "What each week covers. Tap a week to open its lesson.",
      actions: picker,
    })}
    ${termInfo && termPhase(termInfo) !== "in" ? `<p class="note">${icon("info")}<span>${esc(termNote(termInfo))}</span></p>` : ""}
    ${cls && termInfo ? `<section id="year-lesson-totals"></section>` : ""}
    ${nowBanner}
    ${weekNotes}
    <div class="rm-bar">${tabs}${viewTabs}</div>
    ${view === "map" ? mapHtml : `<ol class="wk-list" aria-label="Term ${termTab} weeks">${rows}</ol>`}
    ${legend}`;
  if (cls && termInfo) {
    const host = page.querySelector<HTMLElement>("#year-lesson-totals");
    if (host) {
      const ctx = { classId: cls.id, termId: termInfo.term.id, weekNo: termInfo.current_week ?? 1, nodeId: nowId };
      try {
        const current = await loadWeeklyPreparation(ctx);
        // The other terms' rows (same academic year): their lessons' links now carry the term and week too.
        for (const row of current.available_terms) {
          const n = termNumber(row);
          if (n && current.term.ay && row.ay === current.term.ay && !termWeeks.has(n)) termWeeks.set(n, { id: row.id, weekCount: row.week_count });
        }
        for (const a of page.querySelectorAll<HTMLAnchorElement>("li[data-node] a[href^='/map/'], a.now-banner")) {
          const id = a.closest<HTMLElement>("[data-node]")?.dataset.node ?? nowNode?.id;
          const n = cur.nodes.find((x) => x.id === id);
          if (n) a.setAttribute("href", hrefOf(n));
        }
        const selectedTerm = termNumber(current.term) === termTab ? current.term : current.term.ay ? current.available_terms.find((term) => term.ay === current.term.ay && termNumber(term) === termTab) : undefined;
        if (selectedTerm) {
          const selected = selectedTerm.id === ctx.termId ? current : await loadWeeklyPreparation({ ...ctx, termId: selectedTerm.id, weekNo: 1 });
          await mountWeeklyPreparation(host, { ...ctx, termId: selected.term.id, weekNo: selected.week_no, nodeId: selected.term.id === ctx.termId ? ctx.nodeId : null }, selected, { link: true, totalsOnly: true });
        } else {
          await mountWeeklyPreparation(host, ctx, current, { link: true, totalsOnly: true });
          host.insertAdjacentHTML("afterbegin", `<p class="note">No school term is matched to this Term ${termTab} view. The totals below are for ${esc(current.term.name)}.</p>`);
        }
      } catch (error) {
        host.innerHTML = `<p role="alert">Lesson totals for this view could not be loaded. ${esc((error as Error).message)}</p><button type="button" class="btn secondary" data-year-count-retry>Retry lesson totals</button>`;
        host.querySelector("[data-year-count-retry]")?.addEventListener("click", () => { window.dispatchEvent(new Event("eden:rerender")); });
      }
    }
  }
  // Bring this week into view once the page is on screen.
  page.addEventListener("eden:mounted", () => {
    page.querySelector(".wk-row.now, .rm-stop.rm-now")?.scrollIntoView({ block: "center" });
  }, { once: true });
  // A dot in the strip scrolls to its week (no #hash: the router would redraw the page).
  page.addEventListener("click", (e) => {
    const a = (e.target as HTMLElement).closest<HTMLElement>("[data-rm-jump]");
    if (!a) return;
    e.preventDefault();
    const stop = page.querySelector<HTMLElement>(`#${CSS.escape(a.dataset.rmJump ?? "")}`);
    stop?.scrollIntoView({ behavior: "smooth", block: "center" });
    stop?.classList.add("rm-flash");
    window.setTimeout(() => stop?.classList.remove("rm-flash"), 1600);
  });
  wire(page);
}

function wire(page: HTMLElement): void {
  const sel = page.querySelector("#yp-pick") as HTMLSelectElement | null;
  sel?.addEventListener("change", () => {
    const [kind, val] = sel.value.split(":");
    const href = kind === "class" ? `/roadmap?class=${encodeURIComponent(val!)}` : `/roadmap?grade=${val}`;
    navigate(href);
  });
}

export type { GradeCurriculum };
